/**
 * Standard password controls used everywhere a password is set or shown.
 *
 *  <PasswordField value onChange label="Password" required minLength={6}
 *                 generate            // show a "Generate" button (fills a strong random password)
 *                 hint="…" testid="…" />
 *
 *  <RequireChangeCheckbox checked onCheckedChange />   "Require password change at first login"
 *
 *  <CopyField label="Temporary password" value secret />  read-only value with eye + copy buttons
 *
 *  generatePassword(12) → "Ab3$…" (upper, lower, digit, symbol guaranteed)
 */
import React, { useId, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Eye, EyeSlash, ArrowsClockwise, Copy, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SETS = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '!@#$%&*?'];

export function generatePassword(length = 12) {
  const all = SETS.join('');
  const rnd = (n) => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % n;
    }
    return Math.floor(Math.random() * n);
  };
  const chars = SETS.map((s) => s[rnd(s.length)]); // one of each class
  while (chars.length < length) chars.push(all[rnd(all.length)]);
  for (let i = chars.length - 1; i > 0; i -= 1) { const j = rnd(i + 1); [chars[i], chars[j]] = [chars[j], chars[i]]; }
  return chars.join('');
}

export async function copyText(text, what = 'Copied') {
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
    else {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    toast.success(`${what} copied to clipboard`);
    return true;
  } catch {
    toast.error('Could not copy — select the text and copy it manually');
    return false;
  }
}

function IconBtn({ onClick, title, testid, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
      title={title}
      aria-label={title}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

/**
 * ONE password field for the whole app: the input carries its own tools on the right —
 * [generate] (optional) · [eye] show/hide · [copy]. No confirm field, no separate buttons.
 */
export default function PasswordField({
  value, onChange, label = 'Password', placeholder = 'Enter password', required = false, minLength = 6,
  generate = false, generateLength = 12, copy = true, hint, autoComplete = 'new-password', disabled = false,
  testid = 'password-input', id, className = '', inputClassName = '',
}) {
  const autoId = useId();
  const inputId = id || `pw-${autoId}`;
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const setValue = (v) => onChange?.(v);
  const tools = 1 + (generate ? 1 : 0) + (copy ? 1 : 0);
  const onCopy = async () => {
    if (!value) return;
    if (await copyText(value, 'Password')) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <div className={`field ${className}`}>
      {label && (
        <Label htmlFor={inputId}>
          {label}{required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <div className="relative">
        <Input
          id={inputId}
          type={shown ? 'text' : 'password'}
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          disabled={disabled}
          className={`font-mono ${inputClassName}`}
          style={{ paddingRight: tools * 26 + 6 }}
          data-testid={testid}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {generate && (
            <IconBtn onClick={() => { setValue(generatePassword(generateLength)); setShown(true); }} title="Generate a strong password" testid={`${testid}-generate`} disabled={disabled}>
              <ArrowsClockwise className="w-4 h-4" />
            </IconBtn>
          )}
          <IconBtn onClick={() => setShown((v) => !v)} title={shown ? 'Hide password' : 'Show password'} testid={`${testid}-eye`}>
            {shown ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </IconBtn>
          {copy && (
            <IconBtn onClick={onCopy} title="Copy password" testid={`${testid}-copy`} disabled={!value}>
              {copied ? <Check className="w-4 h-4 text-emerald-600" weight="bold" /> : <Copy className="w-4 h-4" />}
            </IconBtn>
          )}
        </div>
      </div>
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

/** "Require password change at first login" — the standard first-login checkbox. */
export function RequireChangeCheckbox({ checked, onCheckedChange, label = 'Require password change at first login', description = 'The user is asked to set their own password the first time they sign in with this one.', testid = 'require-change-checkbox', id }) {
  const autoId = useId();
  const boxId = id || `rc-${autoId}`;
  return (
    <div className="flex items-start gap-2 rounded border border-border bg-muted/30 px-2.5 py-2">
      <Checkbox id={boxId} checked={!!checked} onCheckedChange={(v) => onCheckedChange?.(v === true)} className="mt-0.5" data-testid={testid} />
      <div className="space-y-0.5">
        <Label htmlFor={boxId} className="cursor-pointer font-medium text-[13px] text-foreground">{label}</Label>
        {description && <p className="field-hint">{description}</p>}
      </div>
    </div>
  );
}

/** Read-only value with copy (and optional show/hide for secrets). */
export function CopyField({ label, value, secret = false, revealed = false, testid = 'copy-field', mono = true, className = '' }) {
  const [shown, setShown] = useState(!secret || revealed);
  const [copied, setCopied] = useState(false);
  const display = shown ? value : '•'.repeat(Math.min(12, String(value || '').length || 8));
  const onCopy = async () => {
    if (await copyText(value, label || 'Value')) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <div className={`field ${className}`}>
      {label && <Label>{label}</Label>}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            readOnly
            value={display || ''}
            onFocus={(e) => e.target.select()}
            className={`w-full h-8 rounded border border-input bg-muted/40 px-2.5 ${secret ? 'pr-8' : ''} text-[13px] ${mono ? 'font-mono' : ''} outline-none focus:border-shell-primary`}
            data-testid={testid}
          />
          {secret && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2">
              <IconBtn onClick={() => setShown((v) => !v)} title={shown ? 'Hide' : 'Show'} testid={`${testid}-eye`}>
                {shown ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </IconBtn>
            </div>
          )}
        </div>
        <Button type="button" variant="outline" className="h-8 px-2.5 flex-shrink-0 text-xs" onClick={onCopy} data-testid={`${testid}-copy`}>
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}
